import CustomButton from "@/components/common/Button";
import CodeEditor from "@/components/common/CodeEditor";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { capitalizeText } from "@/lib/capitalizeText";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { SchemaService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { Card, Checkbox, Form, Input, Tabs } from "antd";
import { Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { Item } = Form;

const SchemaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { schemaName } = useParams<{ schemaName: string }>();
  const [form] = Form.useForm();
  const previewForm = useMemo(() => createForm(), []);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [currentJsonContent, setCurrentJsonContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Utility function to parse JSON data from schema
  const parseSchemaData = (data: any) => {
    if (!data) {
      return null;
    }
    return typeof data === "string" ? JSON.parse(data) : data;
  };

  const isEditMode = !!schemaName;

  const { data: schemaInfo, isLoading } = useQuery({
    queryKey: ["schema", schemaName],
    queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
    enabled: isEditMode && !!schemaName,
  });

  // Fill form in edit mode
  useEffect(() => {
    if (!schemaInfo || !isEditMode) {
      return;
    }

    try {
      const jsonData = schemaInfo.json;
      if (!jsonData) {
        // Set default values even if json is empty
        form.setFieldsValue({
          name: schemaName ?? "",
          is_child: schemaInfo.is_child ?? false,
          json: "",
        });
        return;
      }

      // Parse JSON if it's a string
      const parsed = parseSchemaData(jsonData);
      if (!parsed) {
        // Set default values if parsing fails
        form.setFieldsValue({
          name: schemaName ?? "",
          is_child: schemaInfo.is_child ?? false,
          json: "",
        });
        return;
      }

      // Set form values
      const jsonString = JSON.stringify(parsed, null, 2);
      form.setFieldsValue({
        name: schemaName ?? "",
        is_child: schemaInfo.is_child ?? false,
        json: jsonString,
      });
      setCurrentJsonContent(jsonString);
      setIsFormInitialized(true);
    } catch (error: any) {
      console.error("Error parsing schema data:", error);
      form.setFieldsValue({
        name: schemaName ?? "",
        is_child: schemaInfo.is_child ?? false,
        json: "",
      });
      setCurrentJsonContent("");
      setIsFormInitialized(true);
    }
  }, [schemaInfo, form, schemaName, isEditMode]);

  // Initialize form for new schema creation
  useEffect(() => {
    if (!isEditMode && !isFormInitialized) {
      form.setFieldsValue({
        name: "",
        is_child: false,
        json: "",
      });
      setCurrentJsonContent("");
      setIsFormInitialized(true);
    }
  }, [isEditMode, isFormInitialized, form]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // 1) Validate form
      const values = await form.validateFields();

      // 2) Basic JSON presence check
      if (!values.json || values.json.trim() === "") {
        notify.error("Validation Error", "Please enter JSON data");
        return;
      }

      // 3) Parse JSON safely
      let jsonData: any;
      try {
        jsonData = JSON.parse(values.json);
      } catch (err) {
        notify.error("Invalid JSON", "Please enter valid JSON data");
        console.error("Invalid JSON:", err);
        return;
      }

      // 4) Build payload
      const { _is_child, _json, ...otherValues } = values;
      const payload = {
        ...otherValues,
        ...(values.is_child ? { is_child: true } : {}),
        json: jsonData,
      };

      // 5) Edit vs Create
      if (isEditMode && schemaInfo) {
        const originalData = schemaInfo.json;
        const parsedOriginal = parseSchemaData(originalData);
        if (!parsedOriginal) {
          return;
        }

        if (!checkFormChanges(jsonData, parsedOriginal, notify)) {
          return;
        }

        try {
          const response = await SchemaService.updateSchema({
            schemaName: schemaName ?? "",
            payload,
          });
          if (response) {
            navigate(navigationRoutes.SCHEMAS);
          }
        } catch (apiErr: any) {
          // notify.error("Update Failed", apiErr?.message || "Unable to update schema.");
          console.error("Update schema error:", apiErr);
        }
      } else {
        // >>> Your requested try/catch around createSchema <<<
        try {
          const response = await SchemaService.createSchema({
            schemaName: values.name,
            payload,
          });
          if (response) {
            navigate(navigationRoutes.SCHEMAS);
          }
        } catch (apiErr: any) {
          // notify.error("Create Failed", apiErr?.message || "Unable to create schema.");
          console.error("Create schema error:", apiErr);
        }
      }
    } catch (outerErr: any) {
      console.error("Unexpected handleSubmit error:", outerErr);
      // Handles form.validateFields errors or any unexpected errors above
      // if (outerErr?.errorFields?.length) {
      //   const first = outerErr.errorFields[0];
      //   form.scrollToField(first.name);
      //   notify.error("Validation Error", "Please fix the highlighted fields.");
      // } else {
      //   notify.error("Unexpected Error", outerErr?.message || "Something went wrong.");
      //   console.error("Unexpected handleSubmit error:", outerErr);
      // }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && (isLoading ?? !isFormInitialized)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && !schemaInfo) {
    return <NotFound />;
  }
  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            isEditMode
              ? `Edit ${capitalizeText(schemaName ?? "")}`
              : `Add New Schema`
          }
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={isEditMode ? "Save Schema Changes" : "Save New Schema"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <Item
          name="name"
          label="Schema Name"
          rules={[{ required: true, message: "Please enter schema name" }]}
        >
          <Input placeholder="Enter schema name" disabled={isEditMode} />
        </Item>

        <Item name="is_child" valuePropName="checked">
          <Checkbox disabled={isEditMode}>Is Child Schema</Checkbox>
        </Item>

        <Tabs>
          <Tabs.TabPane tab="JSON Data" key="json">
            <Item
              label="JSON Data"
              name="json"
              required
              validateTrigger="onBlur"
              rules={[
                { required: true, message: "Please enter JSON data" },
                {
                  validator: (_, value) => {
                    try {
                      JSON.parse(value ?? "");
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(new Error("Invalid JSON format"));
                    }
                  },
                },
              ]}
            >
              <CodeEditor
                value={currentJsonContent}
                handleChange={(value: string) => {
                  form.setFieldsValue({ json: value });
                  setCurrentJsonContent(value);
                }}
                language="json"
                key={`json-editor-${isEditMode ? schemaName : "new"}-${isFormInitialized}`}
              />
            </Item>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Preview" key="preview">
            {/* Try to parse JSON, show error if invalid */}
            {(() => {
              if (!isFormInitialized) {
                return null;
              }
              try {
                const parsed = parseSchemaData(currentJsonContent);
                return (
                  <SchemaFormView
                    form={previewForm}
                    schemaJson={parsed}
                    key={`preview-${currentJsonContent}`}
                  />
                );
              } catch (err) {
                return (
                  <div style={{ color: "red", padding: 16 }}>
                    Invalid JSON format for preview. Please fix the JSON to see
                    the preview.
                    <br />
                    <code>
                      {err instanceof Error ? err.message : String(err)}
                    </code>
                  </div>
                );
              }
            })()}
          </Tabs.TabPane>
        </Tabs>
      </Form>
    </Card>
  );
};

export default SchemaFormPage;
